import React from 'react';
import { useRouter } from 'next/router';
import {
  Box, Flex, Heading, Text, useToast,
} from '@chakra-ui/react';
import { GetBasketDocument, useAddItemMutation, useLogEventMutation } from '../../lib/graphql';
import formatMoney from '../../utils/money';
import TicketTierList, { lowestPrice, TicketLine } from './TicketTierList';

interface Props {
  tiers?: any[] | null;
  heading?: string;
}

const plural = (n: number) => `${n} ${n === 1 ? 'ticket' : 'tickets'}`;

/**
 * What to tell the customer when an add never got a usable answer. A network
 * failure says nothing useful in its own words ("Failed to fetch"), so it gets
 * a plain one; a server error's message is shown as the server wrote it.
 */
export const addFailureMessage = (error: any): string => {
  const serverMessage = error?.graphQLErrors?.[0]?.message;
  if (serverMessage) return serverMessage;
  return 'We couldn’t reach the booking system. Check your connection and try again.';
};

/**
 * Ticket selection plus the add-to-basket round trip.
 *
 * Shared by the event detail page and the single-event listing so both offer
 * identical behaviour — the shortfall message and the availability refresh in
 * particular — rather than the listing growing a thinner copy that drifts.
 */
const EventTicketPicker = ({ tiers, heading = 'Tickets' }: Props) => {
  const toast = useToast();
  const router = useRouter();
  const [addItemMutation, { loading: adding }] = useAddItemMutation();
  const [logEventMutation] = useLogEventMutation();
  const from = lowestPrice(tiers);

  // The basket adds one item per call, so a selection spanning several price
  // bands is one call per band. They run in order and stop at the first error,
  // leaving the rest selected so the customer can see what did not go in.
  // Availability is derived from seat rows, so re-read the event to show what
  // an add — and anyone else's — just consumed. The page renders tiers from
  // getInitialProps, so refetching the Apollo query (as this used to) updated
  // a cache nothing read; re-running the page's data load does.
  const refreshAvailability = () => router.replace(router.asPath, undefined, { scroll: false });

  // Best effort: a failed add used to leave no trace anywhere, so there was
  // nothing to diagnose from. Only works signed in; never blocks the customer.
  const reportFailure = (line: TicketLine, error: any) => {
    logEventMutation({
      variables: {
        key: 'add_item_client_error',
        metadata: JSON.stringify({
          ...line,
          message: error?.message ?? String(error),
          network: Boolean(error?.networkError),
          path: router.asPath,
        }),
      },
    }).catch(() => {});
  };

  const addLines = async (lines: TicketLine[]): Promise<boolean> => {
    let added = 0;
    let shortfall = 0;

    for (let i = 0; i < lines.length; i += 1) {
      const { itemId, itemType, quantity } = lines[i];
      let data;
      try {
        // eslint-disable-next-line no-await-in-loop
        ({ data } = await addItemMutation({
          variables: { itemId, itemType, quantity },
          refetchQueries: [{ query: GetBasketDocument }],
        }));
        if (!data?.addItem) throw new Error('addItem returned no result');
      } catch (error) {
        // A thrown add — the network, or a server error outside the response's
        // errors list — used to escape this function unhandled: no toast, the
        // button simply stopped spinning and the basket showed whatever it
        // already held. Say so, and keep the selection for a retry.
        console.error('Add tickets failed:', error);
        reportFailure(lines[i], error);
        toast({
          title: added
            ? `Only ${plural(added)} went into your basket`
            : 'Your tickets were not added',
          description: addFailureMessage(error),
          status: 'error',
          duration: 8000,
          isClosable: true,
        });
        // Earlier bands may already be in the basket, holding seats.
        if (added) refreshAvailability();
        return false;
      }

      const { errors } = data.addItem;
      if (errors?.length) {
        toast({
          title: errors[0].message, status: 'error', duration: 6000, isClosable: true,
        });
        // Earlier bands may already be in the basket, holding seats.
        if (added) refreshAvailability();
        return false;
      }
      const short = data.addItem.shortfall ?? 0;
      shortfall += short;
      added += quantity - short;
    }

    refreshAvailability();

    if (shortfall > 0) {
      toast({
        title: added > 0
          ? `Only ${added} ${added === 1 ? 'seat was' : 'seats were'} still available`
          : 'Those seats have just gone',
        description: added > 0
          ? `We've added ${added} to your basket and held ${added === 1 ? 'it' : 'them'} for you.`
          : undefined,
        status: 'warning',
        duration: 8000,
        isClosable: true,
      });
    } else {
      toast({
        title: `${plural(added)} added to your basket`, status: 'success', duration: 4000, isClosable: true,
      });
    }
    return true;
  };

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="#e7e7f0"
      borderRadius="14px"
      boxShadow="0 1px 2px rgba(45, 44, 122, 0.06), 0 18px 40px -18px rgba(45, 44, 122, 0.30)"
      p={[5, 7]}
    >
      <Flex justify="space-between" align="baseline" mb={4}>
        <Heading as="h2" fontFamily="body" fontSize="22px" m={0}>{heading}</Heading>
        {from !== null && (
          <Text fontSize="14px" color="gray.500">
            From
            {' '}
            {formatMoney(from)}
          </Text>
        )}
      </Flex>
      <TicketTierList tiers={tiers} onAdd={addLines} isAdding={adding} />
    </Box>
  );
};

export default EventTicketPicker;
